import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, Cpu, Loader2, RefreshCw, Search } from 'lucide-react';
import { supportAPI } from '../../services/supportApi';
import { dtfmt } from './supportCommon';
import PrinterTimings from '../printers/PrinterTimings';

/**
 * Support → «Στόλος» (25/10/2026): για ΟΛΑ τα καταστήματα του server ο agent ανά PC (έκδοση, πίσω από την τελευταία,
 * στόχος, τελευταίο γεγονός αναβάθμισης) και η εκτύπωση των τελευταίων ημερών (εργασίες, αποτυχίες, κολλημένες,
 * p50/p90 από ρολόι server). Στόχος έκδοσης για ΕΝΑ κατάστημα τη φορά — μαζικά, δημοσίευση και γενικός διακόπτης μένουν
 * στον super admin. Ανάπτυγμα γραμμής = οι φάσεις ανά εκτυπωτή (ίδιες με την κάρτα καταστήματος).
 */

interface Pc { version: string | null; machine: string | null; connected_since: string | null; printers: string[]; selfUpdate: boolean }
interface Printing { jobs: number; failed: number; retried: number; stuck: number; e2e_p50: number | null; e2e_p90: number | null }
interface FleetStore {
  store_id: number;
  store_name: string;
  organization_name: string | null;
  pcs: Pc[];
  behind: number;
  target_version: string | null;
  target_set_at: string | null;
  last_status: string | null;
  last_detail: string | null;
  last_to: string | null;
  last_at: string | null;
  printing: Printing | null;
}
interface Fleet {
  days: number;
  auto_update_enabled: boolean;
  latest_version: string | null;
  releases: Array<{ version: string; created_at: string; notes: string | null }>;
  stores: FleetStore[];
}

// Γεγονότα αναβάθμισης (print_agent_update_events.status — services/agentUpdates.STATUSES) στα ελληνικά.
const EVENT: Record<string, string> = {
  offered: 'προσφέρθηκε', waiting_idle: 'περιμένει ησυχία', downloading: 'κατεβαίνει', verified: 'ελέγχθηκε', applying: 'εγκαθίσταται',
  done: 'ολοκληρώθηκε', rolled_back: 'επαναφέρθηκε', failed: 'απέτυχε', skipped: 'παραλείφθηκε',
};
// Ίδια με το BLOCKING του server: μετά από αυτά ο στόχος δεν ξαναπροσφέρεται χωρίς νέο ορισμό.
const BAD_EVENTS = ['failed', 'rolled_back'];

/** a παλιότερη από b (x.y.z)· άγνωστη έκδοση = όχι. */
const olderThan = (a: string | null, b: string | null): boolean => {
  if (!a || !b) return false;
  const pa = a.split('.').map(Number); const pb = b.split('.').map(Number);
  if (pa.some(Number.isNaN) || pb.some(Number.isNaN)) return false;
  for (let i = 0; i < 3; i++) if ((pa[i] || 0) !== (pb[i] || 0)) return (pa[i] || 0) < (pb[i] || 0);
  return false;
};
// Πάνω από αυτό το p90 η εκτύπωση θεωρείται αργή (ρολόι server, δημιουργία → ολοκλήρωση).
const SLOW_P90_MS = 8000;

const secs = (ms: number | null) => (ms == null ? '—' : `${(ms / 1000).toFixed(1).replace('.', ',')}″`);
const errText = (e: any, fallback: string) => e?.response?.data?.error || e?.response?.data?.message || fallback;

/** Τι θέλει προσοχή σε ένα κατάστημα (κενό = όλα καλά). */
export const fleetIssues = (s: FleetStore): string[] => {
  const out: string[] = [];
  if (!s.pcs.length) out.push('Κανένα PC συνδεδεμένο');
  if (s.behind) out.push(`${s.behind} PC σε παλιότερη έκδοση`);
  if (s.pcs.some(p => !p.selfUpdate)) out.push('PC χωρίς αυτόματη αναβάθμιση');
  if (s.last_status && BAD_EVENTS.includes(s.last_status)) out.push(`Αναβάθμιση: ${EVENT[s.last_status] || s.last_status}`);
  if (s.printing?.failed) out.push(`${s.printing.failed} αποτυχημένες εκτυπώσεις`);
  if (s.printing?.stuck) out.push(`${s.printing.stuck} κολλημένες στην ουρά`);
  if ((s.printing?.e2e_p90 ?? 0) > SLOW_P90_MS) out.push(`Αργή εκτύπωση (p90 ${secs(s.printing!.e2e_p90)})`);
  return out;
};

const SupportFleetTab: React.FC = () => {
  const [days, setDays] = useState(7);
  const [fleet, setFleet] = useState<Fleet | null>(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [open, setOpen] = useState<number | null>(null);
  const [pick, setPick] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState<number | null>(null);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  const load = async (d = days) => {
    setLoading(true); setFailed(false);
    try { setFleet(await supportAPI.getFleet(d) as unknown as Fleet); } catch { setFailed(true); } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(days); }, [days]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (fleet?.stores || [])
      .filter(s => !q || s.store_name.toLowerCase().includes(q) || (s.organization_name || '').toLowerCase().includes(q) || String(s.store_id) === q)
      .filter(s => !onlyIssues || fleetIssues(s).length > 0)
      // Πρώτα όσα θέλουν προσοχή, μετά το πιο αργό p90.
      .sort((a, b) => (fleetIssues(b).length - fleetIssues(a).length) || ((b.printing?.e2e_p90 ?? -1) - (a.printing?.e2e_p90 ?? -1)));
  }, [fleet, search, onlyIssues]);

  const setTarget = async (s: FleetStore) => {
    const version = pick[s.store_id] || fleet?.latest_version || '';
    if (!version) return;
    setBusy(s.store_id); setNote(null);
    try {
      const r: any = await supportAPI.setAgentTarget(s.store_id, version);
      setNote({ ok: true, text: `#${s.store_id}: στόχος ${version}${r?.offered_now ? ` — προσφέρθηκε τώρα σε ${r.offered_now} PC` : ' — θα προσφερθεί όταν συνδεθεί'}.`
        + (r?.auto_update_enabled === false ? ' Ο γενικός διακόπτης είναι ΚΛΕΙΣΤΟΣ: δεν θα προσφερθεί ώσπου να τον ανοίξει ο super admin.' : '') });
      await load();
    } catch (e) { setNote({ ok: false, text: errText(e, 'Ο στόχος δεν ορίστηκε.') }); } finally { setBusy(null); }
  };
  const clearTarget = async (s: FleetStore) => {
    setBusy(s.store_id); setNote(null);
    try { await supportAPI.clearAgentTarget(s.store_id); setNote({ ok: true, text: `#${s.store_id}: ο στόχος καθαρίστηκε.` }); await load(); }
    catch (e) { setNote({ ok: false, text: errText(e, 'Ο στόχος δεν καθαρίστηκε.') }); } finally { setBusy(null); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-4" data-testid="support-fleet">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-t1 flex items-center gap-2"><Cpu className="h-6 w-6 text-cyan-600 dark:text-cyan-400" />Στόλος</h1>
          <p className="text-sm text-t3">
            Agent ανά PC και εκτύπωση όλων των καταστημάτων{fleet?.latest_version ? ` · τελευταία έκδοση ${fleet.latest_version}` : ''}.
            Στόχος για ένα κατάστημα· μαζικά ο super admin.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-0 sm:flex-none">
            <Search className="h-4 w-4 text-t4 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Κατάστημα, αλυσίδα, #id" aria-label="Αναζήτηση"
              className="w-full sm:w-56 bg-surface-2 dark:bg-surface-1 border border-line text-t1 rounded-lg pl-9 pr-3 py-2 text-sm" />
          </div>
          <select aria-label="Ημέρες" value={days} onChange={(e) => setDays(Number(e.target.value))}
            className="bg-surface-2 dark:bg-surface-1 border border-line text-t1 rounded-lg px-3 py-2 text-sm">
            <option value={1}>24 ώρες</option><option value={7}>7 ημέρες</option><option value={30}>30 ημέρες</option>
          </select>
          <label className="inline-flex items-center gap-2 text-sm text-t2">
            <input type="checkbox" checked={onlyIssues} onChange={(e) => setOnlyIssues(e.target.checked)} data-testid="support-fleet-only-issues" />
            Μόνο όσα θέλουν προσοχή
          </label>
          <button onClick={() => load()} className="p-2 text-t3 hover:text-t1 bg-surface-2 dark:bg-surface-1 border border-line rounded-lg" title="Ανανέωση">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {fleet && !fleet.auto_update_enabled && (
        <p className="text-sm rounded-lg px-3 py-2 bg-amber-500/10 text-amber-800 dark:text-amber-300" data-testid="support-fleet-switch-off">
          Ο γενικός διακόπτης αυτόματης αναβάθμισης είναι κλειστός: κανένας στόχος δεν προσφέρεται ώσπου να τον ανοίξει ο super admin.
        </p>
      )}
      {note && (
        <p role="status" data-testid="support-fleet-note"
          className={`text-sm rounded-lg px-3 py-2 ${note.ok ? 'bg-emerald-500/10 text-emerald-800 dark:text-emerald-300' : 'bg-red-500/10 text-red-800 dark:text-red-300'}`}>{note.text}</p>
      )}

      {loading && !fleet ? (
        <div className="flex items-center justify-center py-24"><RefreshCw className="h-6 w-6 animate-spin text-cyan-600 dark:text-cyan-400" /></div>
      ) : failed ? (
        <p className="text-sm text-red-700 dark:text-red-300 text-center py-12">Ο στόλος δεν φορτώθηκε.</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-t4 text-center py-12">{onlyIssues ? 'Κανένα κατάστημα δεν θέλει προσοχή.' : 'Κανένα κατάστημα.'}</p>
      ) : (
        <ul className="divide-y divide-line border border-line-strong rounded-xl overflow-hidden">
          {rows.map(s => {
            const issues = fleetIssues(s);
            const expanded = open === s.store_id;
            const p = s.printing;
            return (
              <li key={s.store_id} className="bg-surface-2 dark:bg-surface-1" data-testid={`support-fleet-${s.store_id}`}>
                <div className="px-4 py-3 grid grid-cols-1 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1.2fr)] gap-3 items-start">
                  <button type="button" onClick={() => setOpen(expanded ? null : s.store_id)} aria-expanded={expanded}
                    className="text-left flex items-start gap-1.5 min-w-0">
                    {expanded ? <ChevronDown className="h-4 w-4 mt-0.5 text-t3 shrink-0" /> : <ChevronRight className="h-4 w-4 mt-0.5 text-t3 shrink-0" />}
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-t1 truncate">{s.store_name}</span>
                      <span className="block text-xs text-t4">#{s.store_id}{s.organization_name ? ` · ${s.organization_name}` : ''}</span>
                    </span>
                  </button>
                  <div className="text-xs space-y-0.5 min-w-0">
                    {s.pcs.length === 0 ? <span className="text-red-700 dark:text-red-300">Χωρίς σύνδεση</span> : s.pcs.map((pc, i) => {
                      const old = olderThan(pc.version, fleet?.latest_version || null);
                      return (
                        <p key={i} className="truncate" data-testid="support-fleet-pc">
                          <span className={`font-mono px-1.5 py-0.5 rounded ${old ? 'bg-amber-500/15 text-amber-800 dark:text-amber-300' : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'}`}>
                            v{pc.version || '?'}
                          </span>
                          <span className="text-t2"> {pc.machine || 'PC'}</span>
                          <span className="text-t4"> · {pc.printers.length} εκτυπ.{pc.selfUpdate ? '' : ' · χωρίς αυτο-αναβάθμιση'}</span>
                        </p>
                      );
                    })}
                  </div>
                  <div className="text-xs text-t2" data-testid="support-fleet-printing">
                    {p ? (
                      <>
                        <p>{p.jobs} εργασίες · p50 {secs(p.e2e_p50)} · <b className={(p.e2e_p90 ?? 0) > SLOW_P90_MS ? 'text-red-700 dark:text-red-300' : ''}>p90 {secs(p.e2e_p90)}</b></p>
                        {(p.failed > 0 || p.stuck > 0 || p.retried > 0) && (
                          <p className="text-t3">{[p.failed ? `${p.failed} αποτυχίες` : '', p.stuck ? `${p.stuck} κολλημένες` : '', p.retried ? `${p.retried} επαναλήψεις` : ''].filter(Boolean).join(' · ')}</p>
                        )}
                      </>
                    ) : <span className="text-t4">Καμία εκτύπωση στο διάστημα</span>}
                  </div>
                  <div className="text-xs space-y-1.5">
                    <p className="text-t2">
                      Στόχος: <b>{s.target_version || '—'}</b>
                      {s.last_status && <span className={BAD_EVENTS.includes(s.last_status) ? 'text-red-700 dark:text-red-300' : 'text-t3'}>
                        {' '}· {EVENT[s.last_status] || s.last_status}{s.last_to ? ` ${s.last_to}` : ''}{s.last_at ? ` ${dtfmt(s.last_at)}` : ''}
                      </span>}
                    </p>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <select aria-label={`Έκδοση για #${s.store_id}`} value={pick[s.store_id] || fleet?.latest_version || ''}
                        onChange={(e) => setPick(prev => ({ ...prev, [s.store_id]: e.target.value }))}
                        className="bg-surface-2 dark:bg-surface-1 border border-line text-t1 rounded-md px-2 py-1 text-xs">
                        {(fleet?.releases || []).map(r => <option key={r.version} value={r.version}>{r.version}</option>)}
                      </select>
                      <button type="button" onClick={() => setTarget(s)} disabled={busy === s.store_id || !fleet?.releases.length}
                        className="px-2 py-1 rounded-md bg-cyan-600 text-white disabled:opacity-50" data-testid={`support-fleet-target-${s.store_id}`}>
                        {busy === s.store_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Στόχος'}
                      </button>
                      {s.target_version && (
                        <button type="button" onClick={() => clearTarget(s)} disabled={busy === s.store_id}
                          className="px-2 py-1 rounded-md border border-line text-t2 disabled:opacity-50" data-testid={`support-fleet-clear-${s.store_id}`}>Καθαρισμός</button>
                      )}
                    </div>
                  </div>
                </div>
                {issues.length > 0 && (
                  <p className="px-4 pb-2 -mt-1 text-xs text-amber-800 dark:text-amber-300 flex items-start gap-1.5" data-testid={`support-fleet-issues-${s.store_id}`}>
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />{issues.join(' · ')}
                  </p>
                )}
                {expanded && (
                  <div className="px-4 pb-4 border-t border-line pt-3">
                    <PrinterTimings storeId={s.store_id} token={localStorage.getItem('xentrix_token') || ''}
                      path={`/api/support/stores/${s.store_id}/printer-timings`} />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default SupportFleetTab;
